import {
  AsyncSelectOptions,
  type AsyncSelectProps,
} from "@/components/ui/FormFields/AsyncSelectOptions";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Typography,
} from "@/components/ui";
import { SelectDropdownChevron } from "@/components/ui/shadcn/Select/Select";
import { cn } from "@/lib/utils/cn";
import { Check, XIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";

interface SearchableSelectFormFieldGroup<T> {
  label: string;
  items: T[];
}

export interface SearchableSelectFormFieldProps<T> extends AsyncSelectProps {
  className?: string;
  optionsClassName?: string;
  emptyMessage?: ReactNode;
  dropdownFooter?: ReactNode;
  dropdownHeading?: string;
  isItemsError?: boolean;
  isItemsLoading?: boolean;
  items?: T[];
  groupedItems?: SearchableSelectFormFieldGroup<T>[];
  itemsKey: keyof T;
  formatSelectedItemLabel?: (item?: T) => string | undefined;
  formatItemContext?: (item?: T) => string | null | undefined;
  formatItemLabel?: (item?: T) => string | undefined;
  formatItemSearchText?: (item?: T) => string | null | undefined;
  renderItemLabel?: (item: T) => ReactNode;
  formatItemSuffix?: (item?: T) => string | undefined;
  label?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  register: UseFormRegisterReturn;
  buttonClassName?: string;
  dataTestId?: string;
  disabled?: boolean;
  clearable?: boolean;
}

export const SearchableSelectFormField = <T,>({
  className,
  optionsClassName,
  dropdownFooter,
  dropdownHeading,
  emptyMessage = "No results found.",
  isItemsError,
  isItemsLoading,
  items = [],
  groupedItems,
  itemsKey,
  formatItemLabel,
  formatItemSearchText,
  renderItemLabel,
  formatSelectedItemLabel,
  formatItemContext,
  formatItemSuffix,
  label,
  onChange,
  placeholder = "Select an option",
  register,
  buttonClassName,
  dataTestId,
  disabled,
  clearable = false,
  search,
  onSearchChange,
  onOpenChange,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onRetry,
  matchingValues,
}: SearchableSelectFormFieldProps<T>) => {
  const form = useFormContext();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpenState] = useState(false);
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setOpenState(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );
  const isInDisabledFieldset = Boolean(
    triggerRef.current?.closest("fieldset:disabled"),
  );
  const isDisabled = disabled || isInDisabledFieldset;

  useEffect(() => {
    if (!open) {
      return;
    }
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [open]);

  const getItemSearchTarget = (item: T) => {
    const label = formatItemLabel?.(item) ?? formatSelectedItemLabel?.(item);
    const context = formatItemContext?.(item);
    const searchText = formatItemSearchText?.(item);
    return (
      [label, context, searchText].filter(Boolean).join(" ").trim() ||
      String(item[itemsKey])
    );
  };

  const flattenedItems = groupedItems?.flatMap((group) => group.items) ?? items;
  const hasGroupedItems = Boolean(groupedItems?.length);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideClick = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (
        contentRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
      const path = event.composedPath() as Node[];
      const clickedOverlay = path.some(
        (node) =>
          node instanceof Element &&
          node.classList.contains("unique-slideover"),
      );
      if (clickedOverlay) {
        event.stopPropagation();
        event.preventDefault();
      }
    };
    document.addEventListener("pointerdown", handleOutsideClick, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick, true);
    };
  }, [open, setOpen]);

  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => {
        const selectedItem = flattenedItems.find(
          (item) => String(item[itemsKey]) === field.value,
        );
        const selectedLabel =
          selectedItem &&
          (formatSelectedItemLabel?.(selectedItem) ??
            formatItemLabel?.(selectedItem) ??
            String(selectedItem[itemsKey]));

        const renderCommandItem = (item: T) => {
          const itemValue = String(item[itemsKey]);
          if (
            onSearchChange &&
            search?.trim() &&
            !matchingValues?.includes(itemValue) &&
            !getItemSearchTarget(item)
              .toLowerCase()
              .includes(search.trim().toLowerCase())
          )
            return null;
          const isSelected = field.value === itemValue;
          const commandValue = getItemSearchTarget(item);
          const suffix = formatItemSuffix?.(item);

          return (
            <CommandItem
              key={itemValue}
              className={hasGroupedItems ? "pr-8" : undefined}
              value={commandValue || itemValue}
              onSelect={() => {
                field.onChange(itemValue);
                onChange?.(itemValue);
                setOpen(false);
              }}
            >
              <div className="flex items-start gap-2">
                <Check
                  className={cn(
                    "h-4 w-4 shrink-0",
                    hasGroupedItems && "absolute right-2",
                    isSelected ? "text-primary opacity-100" : "opacity-0",
                  )}
                />
                <div className="flex flex-col">
                  {renderItemLabel?.(item) ?? (
                    <Typography
                      className={cn("line-clamp-2 text-sm", optionsClassName)}
                    >
                      {formatItemLabel?.(item) ?? itemValue}
                      {suffix && (
                        <span className="text-muted-foreground">{` ${suffix}`}</span>
                      )}
                    </Typography>
                  )}
                  {formatItemContext?.(item) && (
                    <Typography
                      variant="subHeading"
                      className="text-muted-foreground text-xs"
                    >
                      {formatItemContext(item)}
                    </Typography>
                  )}
                </div>
              </div>
            </CommandItem>
          );
        };

        return (
          <FormItem className={className}>
            {label && (
              <div className="flex">
                <FormLabel>{label}</FormLabel>
                <FormMessage
                  data-testid="error-message"
                  className="grow text-right"
                />
              </div>
            )}
            <Popover
              modal
              open={open}
              onOpenChange={(nextOpen) => {
                if (isDisabled) {
                  return;
                }
                setOpen(nextOpen);
              }}
            >
              <div className="relative">
                <PopoverTrigger asChild>
                  <Button
                    ref={triggerRef}
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={`${field.name}-options`}
                    aria-haspopup="listbox"
                    disabled={isDisabled}
                    data-testid={dataTestId}
                    className={cn(
                      "border-input focus-visible:ring-ring group flex h-10 w-full max-w-full min-w-0 items-center justify-between gap-2 rounded-md border px-3 text-sm transition focus-visible:ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                      buttonClassName,
                      !selectedLabel && "text-muted-foreground",
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate text-left text-[13px] leading-5 font-medium text-inherit">
                      {selectedLabel ?? placeholder}
                    </span>
                    <SelectDropdownChevron />
                  </Button>
                </PopoverTrigger>
                {clearable && field.value && (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-8 -translate-y-1/2"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      form.setValue(register.name, "", {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      onChange?.("");
                    }}
                    disabled={isDisabled}
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              <PopoverContent
                ref={contentRef}
                id={`${field.name}-options`}
                className="w-[--radix-popover-trigger-width] max-w-[min(32rem,calc(100vw-2rem))] min-w-[200px] p-0"
                align="start"
                data-modal-popover="true"
              >
                <Command
                  className="relative w-full"
                  shouldFilter={!onSearchChange}
                >
                  <CommandInput
                    ref={inputRef}
                    placeholder="Search..."
                    value={search}
                    onValueChange={onSearchChange}
                    disabled={isDisabled}
                  />
                  <CommandList
                    className={cn(
                      "w-full",
                      hasGroupedItems &&
                        "[&_[cmdk-group]:not([hidden])~[cmdk-group]:not([hidden])]:border-t",
                    )}
                  >
                    {dropdownHeading && (
                      <div className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                        {dropdownHeading}
                      </div>
                    )}
                    <CommandEmpty>
                      {isItemsLoading ? (
                        <div className="flex justify-center">
                          <LoadingSpinner />
                        </div>
                      ) : isItemsError ? (
                        "Unable to load items."
                      ) : (
                        emptyMessage
                      )}
                    </CommandEmpty>

                    {!isItemsLoading &&
                      !isItemsError &&
                      (hasGroupedItems
                        ? groupedItems?.map((group) => (
                            <CommandGroup
                              key={group.label}
                              heading={group.label}
                            >
                              {group.items.map((item) =>
                                renderCommandItem(item),
                              )}
                            </CommandGroup>
                          ))
                        : items.map((item) => renderCommandItem(item)))}
                    {onLoadMore && (
                      <AsyncSelectOptions
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        onLoadMore={onLoadMore}
                        onRetry={onRetry}
                        isItemsLoading={isItemsLoading}
                        isItemsError={isItemsError}
                      />
                    )}
                  </CommandList>
                </Command>
                {dropdownFooter && (
                  <div className="border-t p-1">{dropdownFooter}</div>
                )}
              </PopoverContent>
            </Popover>
          </FormItem>
        );
      }}
    />
  );
};
